/**
 * Single-choice field for picking a connected calendar. A native <select>
 * can't render a coloured connection-health dot inside its <option>s (see
 * Select.jsx's own doc comment), so this is a small custom listbox instead —
 * field chrome (height/shadow/focus ring/error-replaces-hint) matches
 * Input.jsx, panel positioning follows DropdownMenu.jsx's fixed/portal
 * pattern (needed because the trigger lives inside an overflow:hidden
 * scroll panel).
 * Spec: assets/handoffs/design_handoff_new_calendar_page/README.md §4
 *
 * @param {Array<{uuid:string, label:string, healthy:boolean}>} options - caller pre-shapes label ("Name — username") and healthy (connection status)
 * @param {string} value - selected option's uuid
 * @param {function} onChange - (uuid) => void
 * @param {string=} label
 * @param {string=} hint
 * @param {string=} error
 * @param {string=} placeholder
 * @param {boolean=false} disabled
 */
import React, { useEffect, useLayoutEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./CalendarSelect.module.scss";

const OFFSET = 4;

const CalendarSelect = ({
  options = [],
  value,
  onChange,
  label,
  hint,
  error,
  placeholder = "Select a calendar…",
  disabled = false,
  className,
  id,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const optionRefs = useRef([]);

  const autoId = useId();
  const triggerId = id || autoId;
  const hintId = hint ? `${triggerId}-hint` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const selected = options.find((o) => o.uuid === value) || null;
  const selectedIndex = selected ? options.indexOf(selected) : -1;

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const choose = (option) => {
    onChange?.(option.uuid);
    close();
  };

  useEffect(() => {
    if (!open) return;

    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (optionRefs.current[selectedIndex] ?? optionRefs.current[0])?.focus();
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;

    const reposition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const fitsBelow = spaceBelow >= panelHeight + OFFSET;
      const fitsAbove = spaceAbove >= panelHeight + OFFSET;
      const openUpward = !fitsBelow && (fitsAbove || spaceAbove > spaceBelow);

      setPanelStyle({
        position: "fixed",
        left: triggerRect.left,
        width: triggerRect.width,
        ...(openUpward
          ? { bottom: window.innerHeight - triggerRect.top + OFFSET }
          : { top: triggerRect.bottom + OFFSET }),
      });
    };

    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleOptionKeyDown = (e, index) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      optionRefs.current[(index + 1) % options.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      optionRefs.current[(index - 1 + options.length) % options.length]?.focus();
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  return (
    <div className={classNames(styles.root, className)}>
      {label && (
        <label className={styles.label} htmlFor={triggerId}>
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        className={classNames(styles.trigger, {
          [styles["is-error"]]: !!error,
          [styles["is-disabled"]]: disabled,
          [styles["is-placeholder"]]: !selected,
        })}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        {...rest}
      >
        <span className={styles.triggerLabel}>
          {selected ? (
            <>
              <span
                className={classNames(styles.dot, {
                  [styles["dot--unhealthy"]]: !selected.healthy,
                })}
                aria-hidden="true"
              />
              {selected.label}
            </>
          ) : (
            placeholder
          )}
        </span>
        <Icon name="ChevronDown" size={16} className={styles.chevron} />
      </button>
      {open &&
        createPortal(
          <div role="listbox" ref={panelRef} className={styles.panel} style={panelStyle || { position: "fixed", top: -9999, left: -9999 }}>
            {options.map((option, index) => (
              <button
                key={option.uuid}
                ref={(el) => (optionRefs.current[index] = el)}
                type="button"
                role="option"
                aria-selected={option.uuid === value}
                className={classNames(styles.option, {
                  [styles["is-selected"]]: option.uuid === value,
                })}
                onClick={() => choose(option)}
                onKeyDown={(e) => handleOptionKeyDown(e, index)}
              >
                <span
                  className={classNames(styles.dot, {
                    [styles["dot--unhealthy"]]: !option.healthy,
                  })}
                  aria-hidden="true"
                />
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      {error ? (
        <span id={errorId} className={styles.errorText}>
          {error}
        </span>
      ) : (
        hint && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )
      )}
    </div>
  );
};

export default CalendarSelect;
